import { availableLocales, locale, setLocale, t, type Locale } from '../i18n';
import {
  ChatBubbleIcon,
  ChevronRightIcon,
  CloseIcon,
  TelegramIcon,
  WhatsAppIcon,
} from './Icons';

type Props = {
  onClose: () => void;
  onWebChat: () => void;
  telegramUrl?: string;
  whatsappUrl?: string;
  telegramLabel?: string;
  whatsappLabel?: string;
  brand?: string;
  panelClass?: string;
  isMobile?: boolean;
};

const LOCALE_LABEL: Record<Locale, string> = {
  ru: 'Русский',
  ky: 'Кыргызча',
  en: 'English',
};

export function ChannelPicker({
  onClose,
  onWebChat,
  telegramUrl,
  whatsappUrl,
  telegramLabel,
  whatsappLabel,
  panelClass = '',
  isMobile = false,
}: Props) {
  const current = locale.value;
  const panelStyle = isMobile ? ({ width: '100%' } as const) : ({ width: 320 } as const);
  return (
    <div
      class={`sw-panel${panelClass ? ` ${panelClass}` : ''}`}
      style={panelStyle}
      role="dialog"
      aria-label={t('picker.title')}
    >
      <div class="sw-header">
        <div style={{ flex: 1 }}>
          <div class="sw-header-title">{t('picker.title')}</div>
          <div class="sw-header-sub" style={{ color: 'var(--widget-muted)' }}>
            {t('picker.subtitle')}
          </div>
        </div>
        <button type="button" class="sw-icon-btn" aria-label={t('common.close')} onClick={onClose}>
          <CloseIcon />
        </button>
      </div>
      <div class="sw-channel-list">
        <button type="button" class="sw-channel-item" onClick={onWebChat}>
          <div class="sw-channel-icon primary">
            <ChatBubbleIcon />
          </div>
          <div class="sw-channel-text">
            <div class="sw-channel-label">{t('picker.web')}</div>
            <div class="sw-channel-sub">{t('picker.web_sub')}</div>
          </div>
          <div class="sw-channel-arrow">
            <ChevronRightIcon />
          </div>
        </button>

        {telegramUrl ? (
          <a class="sw-channel-item" href={telegramUrl} target="_blank" rel="noopener noreferrer">
            <div class="sw-channel-icon telegram">
              <TelegramIcon />
            </div>
            <div class="sw-channel-text">
              <div class="sw-channel-label">{t('picker.telegram')}</div>
              <div class="sw-channel-sub">{telegramLabel ?? t('picker.telegram_sub')}</div>
            </div>
            <div class="sw-channel-arrow">
              <ChevronRightIcon />
            </div>
          </a>
        ) : null}

        {whatsappUrl ? (
          <a class="sw-channel-item" href={whatsappUrl} target="_blank" rel="noopener noreferrer">
            <div class="sw-channel-icon whatsapp">
              <WhatsAppIcon />
            </div>
            <div class="sw-channel-text">
              <div class="sw-channel-label">{t('picker.whatsapp')}</div>
              <div class="sw-channel-sub">{whatsappLabel ?? t('picker.whatsapp_sub')}</div>
            </div>
            <div class="sw-channel-arrow">
              <ChevronRightIcon />
            </div>
          </a>
        ) : null}
      </div>
      <div class="sw-footer sw-footer--locales">
        <div class="sw-locale-tabs" role="tablist" aria-label={t('picker.language')}>
          {availableLocales.map((code) => (
            <button
              key={code}
              type="button"
              role="tab"
              class={`sw-locale-tab${code === current ? ' sw-locale-tab--active' : ''}`}
              onClick={() => setLocale(code, true)}
              aria-selected={code === current}
            >
              {LOCALE_LABEL[code]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
